import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { SpendTableColumn, SortOrderItem } from './VirtualizedSpendTable';

interface LockedTableHeaderProps<T> {
  columns: SpendTableColumn<T>[];
  sortOrder: SortOrderItem[];
  onSort: (field: string, event: React.MouseEvent) => void;
  variant?: 'default' | 'magazine';
}

// Shared component for locked table header - ensures perfect alignment
export function LockedTableHeader<T>({ columns, sortOrder, onSort, variant = 'magazine' }: LockedTableHeaderProps<T>) {
  const renderSortIcon = (field: string) => {
    const sortItem = sortOrder.find(s => s.field === field);
    if (!sortItem) return null;
    const isAsc = sortItem.direction === 'asc';
    const sortIndex = sortOrder.findIndex(s => s.field === field);
    return (
      <div className="flex items-center">
        {isAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {sortOrder.length > 1 && (
          <span className="text-xs font-bold ml-1 theme-text-muted">
            {sortIndex + 1}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="virtualized-table overflow-x-hidden">
      <table className="w-full theme-text-light table-fixed" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={{ width: column.width || 'auto' }} />
          ))}
        </colgroup>
        <thead>
          <tr className="sticky top-0 z-10 table-header bg-[var(--bg-elev)]">
            {columns.map((column) => {
              // All headers are center aligned and evenly spaced
              return (
                <th
                  key={column.key}
                  className={`text-sm font-semibold theme-text-light cursor-pointer text-center justify-center
                    ${variant === 'magazine' ? '' : 'uppercase tracking-wider'}
                    ${column.sortable ? '' : ''}`}
                  onClick={(e) => column.sortable && onSort(column.key, e)}
                  style={{ width: column.width || 'auto', paddingTop: '22px', paddingBottom: '22px', paddingLeft: '24px', paddingRight: '24px' }}
                >
                  <div className="flex items-center justify-center gap-2">
                    {column.label}
                    {column.sortable && renderSortIcon(column.key)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
      </table>
    </div>
  );
}

